import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Container, Button, Spinner, Nav } from 'react-bootstrap';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaMinusCircle } from 'react-icons/fa';
import { Column } from 'react-table';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { RoleEnum, UserDTO } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import {
	Markets,
	ReactSelectOption,
	RemoteSelectOption,
	SelectOptions,
} from '../../types';
import { arrayToOptions, stringToOption } from '../../utils';
import toaster from '../../utils/toaster';
import { useApiClient } from '../../hooks/useApiClient';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { userActionsColumnsData } from '../../components/UserActions/userActionsColumnsData';
import UserArea from '../../components/User/UserArea';
import UserActions from '../../components/UserActions/UserActions';
import { OpportunityField } from '../../api/models/AccountEntity';
import useStore from '../../store/TiassistoStore';
import { useFetchVehicles } from '../../hooks/useFetchVehicles';
import { buildUserFormValues } from '../../utils/userFormUtils';
import _ from 'lodash';
// @ts-ignore
import './UserFormPage.scss';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { FaX } from 'react-icons/fa6';
import { useConfirmAction } from 'src/hooks/useConfirmAction';
import { useQueryClient } from '@tanstack/react-query';
import { isMarket } from 'src/utils/markets';
import { RejectUserModal } from 'src/components/User/RejectUserModal';
import { ResetUserPassword } from 'src/components/User/ResetUserPassword';
import { ReporterOpportunityTab } from '../ReporterDetailsPage/ReporterDetailsPage';
import { hasMissingRequiredDocuments } from 'src/components/Reporter/reporterUtils';
import { ReporterStatusEnum } from 'src/api/models/Enums';

const REPORTER_DETAILS_TAB = "reporter_details_tab";
const REPORTER_OPPORTUNITY_TAB = "reporter_opportunity_tab";

const UserFormPage = () => {
	// the username  must be an email address
	const { username } = useParams<{ username: string }>();
	const navigate = useNavigate();
	const { apiClient, handleApi } = useApiClient();
	const [response, setResponse]: [any, any] = useState(null);
	const [responseStoricoAzioni, setResponseStoricoAzioni]: [any, any] =
		useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingLogs, setLoadingLogs] = useState(true);
	const [referralPartners, setReferralPartners] = useState<ReactSelectOption[]>(
		[]
	);
	const [backofficeUsers, setBackofficeUsers] = useState<
		(ReactSelectOption & { subtitle: string })[]
	>([]);
	const [userSales, setUserSales] = useState([] as any[]);
	const [errorLogs, setErrorLogs] = useState(null);
	const [errorUser, setErrorUser] = useState(null);
	const [companyAccountsFetched, setCompanyAccountsFetched] = useState(false);
	const [companyAccounts, setCompanyAccounts] = useState<any[]>([]);
	const [accountAziendaId, setAccountAziendaId] = useState<string | null>(null);
	const { userRole, user, userReferencePartnerList, canApproveSegnalatori, isSalesStellantis, userVisibilityTag } = useAuth();
	const [initialValues, setInitialValues]: [any, Function] = useState({});
	const [roles, setRole] = useState<SelectOptions[]>();
	const [companyDetail, setCompanyDetail] = useState<any[]>();
	const [itsMe, setItsMe] = useState(false);
	const newUser = username !== 'new' ? false : true;
	const { t } = useTranslation();
	const { recordTypeAziendaId, setRecordTypeAziendaId } = useStore();
	const { fetchVehiclesRecursively } = useFetchVehicles();
	const { showError } = useErrorToast();
	const { confirmAction, isConfirmShown } = useConfirmAction();
	const [isApprovingUser, setIsApprovingUser] = useState(false);
	const queryClient = useQueryClient();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const isNewSegnalatore = (params.get('role') ?? '') === RoleEnum.SEGNALATORE;
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [segnalatoreActiveKey, setSegnalatoreActiveKey] = useState<string>(REPORTER_DETAILS_TAB);
	const showSegnalatoreTabs = response?.role === RoleEnum.SEGNALATORE
	const [allBackofficeUsers, setAllBackofficeUsers] = useState<(ReactSelectOption & { subtitle: string })[]>([]);

	const canEdit = [
		RoleEnum.MASTER,
		RoleEnum.BACKOFFICE,
		RoleEnum.ADMIN,
		RoleEnum.SALES_DEALER,
		RoleEnum.AREA_MANAGER,
	].includes(userRole);

	const canApproveOrReject = [
		RoleEnum.ADMIN,
		RoleEnum.MASTER,
		// RoleEnum.BACKOFFICE,
		// RoleEnum.AREA_MANAGER,
	].includes(userRole) || canApproveSegnalatori;

	const canResetPassword =
		!newUser &&
		!itsMe &&
		[RoleEnum.MASTER, RoleEnum.ADMIN].includes(userRole);

	async function getAccountsCompany(recordTypeAziendaId) {
		const response =
			await apiClient.salesforceAccountController.getAccountsCompany(
				recordTypeAziendaId
			);
		return response.data;
	}

	const initialValuesAllRoles = {
		username: '',
		role: '',
		given_name: '',
		family_name: '',
		phone: '',
		phone_backup: '',
		email_backup: '',
		group: [],
	};

	const initialValuesMaster = {
		...initialValuesAllRoles,
		reference_partner: [],
	};

	const initialValuesBackoffice = {
		...initialValuesAllRoles,
		reference_partner: [],
		can_reassign_leads: false,
	};

	const initialValuesAreaManager = {
		...initialValuesBackoffice,
		area_backoffices: [],
	};

	const initialValuesSales = {
		...initialValuesAllRoles,
		reference_partner: [],
		reference_backoffice: '',
		visibility_tag: [],
		is_active: false,
		is_salesforce_sales: false,
		display_commissions: true,
	};

	const initialValuesSegnalatore = {
		...initialValuesAllRoles,
		reference_partner: (userReferencePartnerList && userReferencePartnerList.length === 1)
			? userReferencePartnerList?.map((item) => ({
				value: item,
				label: item,
			}))
			: [],
		reference_sales_dealer: '',
		codice_fiscale: '',
		partita_iva: '',
		provvigioni_segnalatore: '',
		provvigioni_dealer: '',
		iban: '',
		business_name: '',
		reference_backoffice: '',
	};

	const initialValuesFleetManager = {
		...initialValuesAllRoles,
		sales: [],
		business_name: '',
		is_active_email: true,
		is_active: false,
		company_detail: '',
	};

	const fetchAndStoreSFCompanyAccounts = (recordTypeAziendaId) => {
		const callGetAccountsCompany = async () => {
			// if (!companyAccountsFetched) {
			try {
				const accounts = await getAccountsCompany(recordTypeAziendaId);
				setCompanyAccounts(accounts);
				setCompanyAccountsFetched(true);
			} catch (error) {
				console.error('Error fetching company accounts:', error);
			}
			// }
		};
		callGetAccountsCompany();
	};

	const fetchAndStoreReferencePartners = async (newRole) => {
		try {
			if (newRole === RoleEnum.SEGNALATORE) {
				handleApi(apiClient.userController.getReporterDealers())
					.then((options: RemoteSelectOption[]) => {
						setReferralPartners(options);
					})
					.catch((err) =>
						toaster.error(
							err.message || i18next.t('error_loading_account_data')
						)
					);
			} else {
				handleApi(apiClient.salesforceAccountController.getAccountFields())
					.then((res: OpportunityField) => {
						const options: ReactSelectOption[] = res[0]?.picklistValues?.map(
							(el) => ({ label: el.label, value: el.value })
						);

						setReferralPartners(options);
					})
					.catch((err) =>
						toaster.error(
							err.message || i18next.t('error_loading_account_data')
						)
					);
			}
		} catch (error) { }
	};

	const customRolesByCountry = isMarket(Markets.it) ? [RoleEnum.SEGNALATORE] : [];

	useEffect(() => {
		handleApi(apiClient.userController.getBackofficeUsers())
			.then((res: UserDTO[]) => {
				const options: any[] = res.map((user) => ({
					label: `${user.given_name} ${user.family_name}`,
					value: user.username,
					subtitle: user.username,
				}));

				setAllBackofficeUsers(options);
			})
			.catch((err) => {
				console.error(err);
				toaster.error(err.message || i18next.t('error_loading_backoffice_data'));
			});
	}, [])

	useEffect(() => {
		if (userRole === RoleEnum.ADMIN) {
			setRole(
				arrayToOptions([
					RoleEnum.ADMIN,
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.FLEET_MANAGER,
					RoleEnum.AREA_MANAGER,
				].concat(customRolesByCountry))
			);
		}
		if (userRole === RoleEnum.MASTER) {
			setRole(
				arrayToOptions([
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.FLEET_MANAGER,
					RoleEnum.AREA_MANAGER,
				].concat(customRolesByCountry))
			);
		}
		if ([RoleEnum.BACKOFFICE].includes(userRole)) {
			setRole(
				arrayToOptions([
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.FLEET_MANAGER,
					RoleEnum.AREA_MANAGER,
				].concat(customRolesByCountry))
			);
		}
		if (userRole === RoleEnum.AREA_MANAGER) {
			setRole(
				arrayToOptions([
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
				].concat(customRolesByCountry))
			);
		}
		// for now Sales cannot manage users
		if (
			[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
				userRole
			)
		) {
			setRole(arrayToOptions(['']));
		}
		// for now FLEET_MANAGER cannot manage users
		if (userRole === RoleEnum.FLEET_MANAGER) {
			setRole(arrayToOptions([]));
		}

		// flag tipologia in fleet manager form
		setCompanyDetail(arrayToOptions(['FLEET_MANAGER', 'AGENTE']));

		if (
			[
				RoleEnum.ADMIN,
				RoleEnum.MASTER,
				RoleEnum.BACKOFFICE,
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
				RoleEnum.AREA_MANAGER,
			].includes(userRole)
		) {
			// GET REFERENCE PARTNERS
			handleApi(apiClient.salesforceAccountController.getAccountFields())
				.then((res: OpportunityField) => {
					const options: ReactSelectOption[] = res[0]?.picklistValues?.map(
						(el) => ({ label: el.label, value: el.value })
					);

					setReferralPartners(options);
				})
				.catch((err) =>
					toaster.error(err.message || i18next.t('error_loading_account_data'))
				);

			// GET SALES
			handleApi(apiClient.userController.getRegisterSales())
				.then((res) => {
					const userSales = res.map((user) =>
						stringToOption(
							user.given_name + ' ' + user.family_name,
							user.username
						)
					);
					setUserSales(userSales);
				})
				.catch((err) =>
					toaster.error(err.message || i18next.t('error_loading_sales_data'))
				);

			// GET RECORD TYPE FLEET_MANAGER
			handleApi(apiClient.salesforceAccountController.getAccountTypes()).then(
				async (res) => {
					const recordTypeAziendaId = res?.data?.filter(
						(acc) => acc.Name === 'Azienda'
					)[0]?.Id;
					setRecordTypeAziendaId(recordTypeAziendaId);
				}
			);

			// GET STORICO AZIONI
			// handleApi(apiClient.vehicleController.getVehiclesFilter())
			fetchVehiclesRecursively()
				.then((res) => {
					const storico_azioni = res.flatMap((vehicle) =>
						vehicle.storico_azioni
							.filter(
								(azione) => (itsMe ? user.username : username) === azione.utente
							)
							.map((azione) => ({
								...azione,
								type: azione.type.replace('_', ' '),
								id: vehicle.id,
								marca: vehicle.marca,
								modello: vehicle.modello,
								nr_ordine: vehicle.nr_ordine,
								allestimento: vehicle.allestimento,
								codice_costruttore: vehicle.codice_costruttore,
								telaio: vehicle.telaio,
								targa: vehicle.targa,
								dealer: vehicle.dealer,
							}))
					);

					setLoadingLogs(false);
					setResponseStoricoAzioni(storico_azioni);
				})
				.catch((err) => {
					setErrorLogs(err);
					setLoading(false);
					toaster.error(err.message || i18next.t('error_loading_data'));
				});
		}

		if (newUser) {
			// if new
			const initVal = {
				username: '',
				role: '',
				given_name: '',
				family_name: '',
				phone: '',
			};

			if (userRole === RoleEnum.SALES_DEALER) {
				Object.assign(initVal, {
					...initialValuesSegnalatore,
					role: RoleEnum.SEGNALATORE,
				});
			}

			setInitialValues(initVal);
			setLoading(false);
		} else {
			// GET SINGLE USER
			// edit mode
			handleApi(apiClient.userController.getRegisterSingleUser(username!))
				.then((res) => {
					user.username === res.username && setItsMe(true);

					if (!res.sales || !Array.isArray(res.sales)) {
						res.sales = [];
					}

					if (
						res.role === RoleEnum.FLEET_MANAGER &&
						_.isString(res.business_name)
					) {
						handleApi(
							apiClient.salesforceAccountController.getAccount(
								res.business_name
							)
						)
							.then((accountRes) => {
								const businessName = accountRes?.data[0]?.Name;
								// map businessName from SF to the id saved into user in cognito
								// then create the object for the react-select option
								// this is a temp fix only for those account whose business_name is a string
								const businessOptions = {
									label: businessName,
									value: res.business_name,
								};
								const updatedUser = {
									...res,
									business_name: businessOptions,
								};
								setResponse(updatedUser);
							})
							.catch((err) => {
								console.error('err', err);
							});
					} else {
						setResponse(res);
					}

					setLoading(false);
				})
				.catch((err) => {
					setErrorUser(err);
					setLoading(false);
					toaster.error(err.message || i18next.t('error_loading_user_data'));
				});
		}

		// GET STORICO AZIONI
		fetchVehiclesRecursively()
			.then((res) => {
				const storico_azioni = res.flatMap((vehicle) =>
					vehicle.storico_azioni
						.filter(
							(azione) => (itsMe ? user.username : username) === azione.utente
						)
						.map((azione) => {
							return {
								...azione,
								type: azione.type.replace('_', ' '),
								id: vehicle.id,
								marca: vehicle.marca,
								modello: vehicle.modello,
								nr_ordine: vehicle.nr_ordine,
								allestimento: vehicle.allestimento,
								codice_costruttore: vehicle.codice_costruttore,
								telaio: vehicle.telaio,
								targa: vehicle.targa,
								dealer: vehicle.dealer,
							};
						})
				);

				setLoadingLogs(false);
				setResponseStoricoAzioni(storico_azioni);
			})
			.catch((err) => {
				setErrorLogs(err);
				setLoading(false);
				toaster.error(err.message || i18next.t('error_loading_data'));
			});

		if (
			[
				RoleEnum.MASTER,
				RoleEnum.ADMIN,
				RoleEnum.BACKOFFICE,
				RoleEnum.AREA_MANAGER,
			].includes(userRole)
		) {
			if (userRole === RoleEnum.BACKOFFICE) {
				const userAttrs = user.attributes;
				setBackofficeUsers([
					{
						label: `${userAttrs.given_name} ${userAttrs.family_name}`,
						value: userAttrs.email,
						subtitle: userAttrs.email,
					},
				]);
			} else {
				handleApi(apiClient.userController.getRegisterUsers())
					.then((res: UserDTO[]) => {
						res = res.filter((el) => el.role === RoleEnum.BACKOFFICE);
						const options: any = res.map((el) => {
							return {
								label: `${el.given_name} ${el.family_name}`,
								value: el.username,
								subtitle: el.username,
							};
						});

						setBackofficeUsers(options);
					})
					.catch((err) => {
						toaster.error(err.message || i18next.t('cannot_load_partners'));
					});
			}
		}
	}, []);

	useEffect(() => {
		// if (response && userSales.length > 0 && referralPartners.length > 0) {
		// 	const sales = response.sales?.map(user => {

		// 		return userSales.find((sale) => {
		// 			return user === sale.originalValue;
		// 		});
		// 	});

		// 	const referencePartnerIds = response.reference_partner || [];
		// 	const partnerValues: ReactSelectOption[] = referencePartnerIds.map((el) => {
		// 		const foundPartner = referralPartners.find(r => r.value === el);
		// 		return {
		// 			label: foundPartner ? foundPartner.label : '',
		// 			value: el
		// 		};
		// 	});

		// 	let referenceBackoffice: ReactSelectOption | null = null;
		// 	if (response.reference_backoffice) {
		// 		if (backofficeUsers.length) {
		// 			referenceBackoffice = backofficeUsers.find(el => el.value === response.reference_backoffice) || null;
		// 		} else {
		// 			referenceBackoffice = { value: response.reference_backoffice, label: response.reference_backoffice };
		// 		}
		// 	}

		// prepare data for the intiial values to feed the form fields
		if (response && (referralPartners.length || backofficeUsers.length)) {
			const initialValues = buildUserFormValues({
				role: response.role,
				response,
				backofficeUsers,
				userSales,
				companyDetail,
			});

			setInitialValues(initialValues);
		}
	}, [response, userSales, referralPartners, backofficeUsers]);

	useEffect(() => {
		if (!recordTypeAziendaId) return;
		fetchAndStoreSFCompanyAccounts(recordTypeAziendaId);
	}, [recordTypeAziendaId]);

	const submitFnFactory = (username) => {
		// if edit
		return (values, { setSubmitting, isSubmitting }) => {
			let body: UserDTO;
			body = { ...values };

			if (values.reference_sales_dealer) {
				body.reference_sales_dealer = values.reference_sales_dealer.value;
			}

			if (
				[
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.SEGNALATORE,
					RoleEnum.AREA_MANAGER,
				].includes(values.role)
			) {
				// reference partner is an array of strings
				body.reference_partner = values.reference_partner.map((el) => el.value);
			} else {
				const { reference_partner, ...bodyFiltered } = body;
				body = { ...bodyFiltered };
			}

			body.area_backoffices = [RoleEnum.AREA_MANAGER].includes(values.role)
				? (values.area_backoffices || []).map((el) => el.value).filter(Boolean)
				: [];

			body.group = values.group?.length ? (values.group || []).map((el) => el.value).filter(Boolean) : undefined;

			if (
				[
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.SEGNALATORE,
				].includes(values.role)
			) {
				if (values.reference_backoffice) {
					body.reference_backoffice = values.reference_backoffice.value;
				}
			} else {
				const { reference_backoffice, ...bodyFiltered } = body;
				body = { ...bodyFiltered };
			}

			if (!body.reference_backoffice) {
				body = _.omit(body, ['reference_backoffice'])
			}

			if (values.role === RoleEnum.SEGNALATORE && _.isEmpty(body.iban)) {
				toaster.error(i18next.t('iban_required'));
				setSubmitting(false);
				return;
			}

			if (values.role === RoleEnum.SEGNALATORE && _.isEmpty(body.business_name)) {
				toaster.error(i18next.t('business_name_required'));
				setSubmitting(false);
				return;
			}

			if (values.role === RoleEnum.SEGNALATORE && !body.documents?.length) {
				toaster.error(i18next.t('segnalatore_all_documents_required'));
				setSubmitting(false);
				return;
			}

			if (values.role === RoleEnum.SEGNALATORE && hasMissingRequiredDocuments(userRole, body)) {
				toaster.error(i18next.t('segnalatore_all_documents_required'));
				setSubmitting(false);
				return;
			}

			// EDIT USER MODE
			if (!newUser) {
				if (values.role === RoleEnum.FLEET_MANAGER && values.sales) {
					const sales = values.sales.map((sale) => sale.originalValue);
					const business_name = values.business_name.value;
					const company_detail = values.company_detail.value;
					body = { ...values, sales, company_detail, business_name };
				}

				handleApi(apiClient.userController.putRegisterUsers(username, body))
					.then((res) => {
						GTMsendEvent({
							event: 'update_user',
							category: 'user',
							action: 'update',
							label: 'update_user',
						});

						toaster.success(i18next.t('user_updated_success', { username }));
						setTimeout(() => navigate(0), 1500);
					})
					.catch((err) => showError(err, i18next.t('error_updating_user')))
					.finally(() => setSubmitting(false));
			}

			// NEW USER MODE
			else {
				if (values.role === RoleEnum.FLEET_MANAGER && values.sales.length) {
					// the SALES select field must show given_name + family_name but it sends the username
					// if sales select not multi then this
					// const salesUsername = userSales.find(userSale => userSale.value === values.sales)
					const sales = values.sales.map((sale) => sale.originalValue);
					const company_detail = values.company_detail.value;

					body = {
						...values,
						sales,
						company_detail,
						business_name: values.business_name.value,
					};
				}

				// if (values.role === RoleEnum.SEGNALATORE && userRole === RoleEnum.SALES_DEALER && isSalesStellantis) {
				// 	body.visibility_tag = userVisibilityTag;
				// }

				handleApi(apiClient.userController.postRegisterUsers(body))
					.then((res) => {
						GTMsendEvent({
							event: 'complete_new_user',
							category: 'user',
							action: 'complete',
							label: 'new_user',
						});
						toaster.success(
							i18next.t('user_created_success', { username: res.username })
						);
						if (userRole === RoleEnum.SALES_DEALER) {
							navigate(`/my-reporters`)
						} else {
							navigate(`/manage-users`);
						}
					})
					.catch((err) => showError(err, i18next.t('error_creating_user')))
					.finally(() => setSubmitting(false));
			}
		};
	};

	const deleteUser = () => {
		return handleApi(apiClient.userController.deleteRegisterUsers(username!))
			.then((res) => {
				GTMsendEvent({
					event: 'delete_user',
					category: 'user',
					action: 'delete',
					label: `delete_user`,
				});
				toaster.success(i18next.t('user_deleted_success', { username }));
				navigate(`/manage-users`);
			})
			.catch((err) => {
				showError(err, i18next.t('error_deleting_user'));
			});
	};

	const actionsColumns = useMemo(
		() => userActionsColumnsData as Column<Object>[],
		[]
	);
	const actionsData = useMemo(
		() => responseStoricoAzioni ?? [],
		[responseStoricoAzioni]
	);

	const handleRoleChange = (newRole, setFieldValue, values) => {
		const isMasterCreatingSegnalatore = newRole === RoleEnum.SEGNALATORE && userRole === RoleEnum.MASTER;
		const initialValues = {
			ALL_ROLES: initialValuesAllRoles,
			MASTER: initialValuesMaster,
			BACKOFFICE: initialValuesBackoffice,
			AREA_MANAGER: initialValuesAreaManager,
			SALES: initialValuesSales,
			SALES_DEALER: initialValuesSales,
			SALES_INTERNAL: initialValuesSales,
			SEGNALATORE: Object.assign(initialValuesSegnalatore,
				isMasterCreatingSegnalatore ? { reference_partner: [] } : {}
			),
			FLEET_MANAGER: initialValuesFleetManager,
		};

		const roleKey = newRole?.toUpperCase() || 'ALL_ROLES';
		const newInitialValues = initialValues[roleKey] || initialValuesAllRoles;

		setInitialValues({
			...newInitialValues,
			username: values.username,
			role: newRole, // Use newRole here
			given_name: values.given_name,
			family_name: values.family_name,
			phone: values.phone,
			phone_backup: values.phone_backup,
			email_backup: values.email_backup,
			is_active: newUser ? newInitialValues.is_active : (response?.is_active || false)
		});

		// fetch all company accounts if user is fleet manager
		if (newRole === RoleEnum.FLEET_MANAGER) {
			fetchAndStoreSFCompanyAccounts(recordTypeAziendaId);
		}
	};

	const handleReferencePartnerChange = (
		newReferencePartner,
		setFieldValue,
		values
	) => {
		// @todo: fetch by reference partner
		console.log({ newReferencePartner, values });
	};

	const isAwaitingApproval = useMemo(() => {
		if (!response) return false;
		return !response.is_active && response?.role === RoleEnum.SEGNALATORE;
	}, [response])

	const showDeleteAction = useMemo(() => {
		if (isAwaitingApproval) return false;

		return username !== 'new' && !itsMe && canEdit;
	}, [itsMe, username, isAwaitingApproval]);

	const approveUser = async () => {
		if (isConfirmShown) return;

		const confirmed = await confirmAction({
			title: i18next.t('confirm'),
			message: i18next.t('approve_user_confirmation_message'),
		});

		if (!confirmed) return;

		setIsApprovingUser(true);
		handleApi(apiClient.userController.approveUser(username!))
			.then((res) => {
				GTMsendEvent({
					event: 'approve_user',
					category: 'user',
					action: 'update',
					label: 'approve_user',
				});

				toaster.success(i18next.t('user_updated_success', { username }));
				queryClient.invalidateQueries({ queryKey: ['reporters_list'] });
				setTimeout(() => navigate(`/manage-users`), 1500);
			})
			.catch((err) => showError(err, i18next.t('error_updating_user')))
			.finally(() => setIsApprovingUser(false));
	}

	const rejectUser = async (reason: string) => {
		if (isConfirmShown) return;

		handleApi(apiClient.userController.rejectUser(username!, reason))
			.then((res) => {
				GTMsendEvent({
					event: 'reject_user',
					category: 'user',
					action: 'update',
					label: 'reject_user',
				});

				toaster.success(i18next.t('user_updated_success', { username }));
				queryClient.invalidateQueries({ queryKey: ['reporters_list'] });
				setTimeout(() => navigate(`/manage-users`), 1500);
			})
			.catch((err) => showError(err, i18next.t('error_updating_user')))
	}

	const hasReferenceBackoffice = useMemo(() => {
		return !!initialValues.reference_backoffice;
	}, [initialValues])

	return (
		<Container className="user-form-page-container" id="user-form-page" fluid>
			{[
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
				RoleEnum.FLEET_MANAGER,
			].includes(userRole) ? (
				<Breadcrumb
					item1={{ name: i18next.t('back'), link: '/' }}
					activeItem={{ name: '' }}
				></Breadcrumb>
			) : (
				<Breadcrumb
					item1={{ name: i18next.t('manage_users'), link: '/manage-users' }}
					activeItem={{ name: i18next.t('user') }}
				></Breadcrumb>
			)}

			{errorUser && <Navigate to="/not-authorized" replace />}

			{!loading && initialValues && (
				<div className="mb-3 clearfix">
					<span className="display-6">
						{response
							? itsMe
								? i18next.t('my_area')
								: response?.username
							: (isNewSegnalatore ? i18next.t('create_new_reporter') : i18next.t('create_new_user'))}
					</span>

					{(showDeleteAction || canResetPassword) && (
						<span className="float-end mt-2 d-flex gap-2">
							{canResetPassword && <ResetUserPassword username={username!} />}
							{showDeleteAction && (
								<Button onClick={deleteUser}>
									<FaMinusCircle style={{ marginRight: '8px' }} />
									{t('delete_user')}
								</Button>
							)}
						</span>
					)}
					{isAwaitingApproval && canApproveOrReject && (
						<div className="d-flex align-items-center justify-content-end mt-2">
							<Button onClick={approveUser} size="sm" variant="success" disabled={isApprovingUser || !hasReferenceBackoffice}>
								{isApprovingUser ? <Spinner animation="border" size="sm" /> : <FaCheck style={{ marginRight: '8px' }} />}
								{t('approve_user')}
							</Button>
							<Button onClick={() => setShowRejectModal(true)} className="ms-2" size="sm" variant="danger" disabled={isApprovingUser}>
								<FaX style={{ marginRight: '8px' }} />
								{t('reject_user')}
							</Button>
						</div>
					)}
				</div>
			)}

			{showSegnalatoreTabs &&
				(<Nav fill variant="tabs"
					activeKey={segnalatoreActiveKey}
					className="mt-4"
					onSelect={(key) => setSegnalatoreActiveKey(key || REPORTER_DETAILS_TAB)}
				>
					<Nav.Item>
						<Nav.Link eventKey={REPORTER_DETAILS_TAB}>{i18next.t('reporter_details_tab')}</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link eventKey={REPORTER_OPPORTUNITY_TAB}>{i18next.t('reporter_opportunity_tab')}</Nav.Link>
					</Nav.Item>
				</Nav>)}

			{/* MY AREA SECTION */}
			{((showSegnalatoreTabs && segnalatoreActiveKey === REPORTER_DETAILS_TAB) || !showSegnalatoreTabs) &&
				<UserArea
					isReadonly={!canEdit}
					initialValues={initialValues}
					documents={initialValues.documents}
					username={username}
					submitFnFactory={submitFnFactory}
					loading={loading}
					stringToOption={stringToOption}
					itsMe={itsMe}
					roles={roles}
					backofficeUsers={backofficeUsers}
					allBackofficeUsers={allBackofficeUsers}
					userSales={userSales}
					companyDetail={companyDetail}
					referralPartners={referralPartners}
					handleRoleChange={handleRoleChange}
					recordTypeAziendaId={recordTypeAziendaId}
					handleReferencePartnerChange={handleReferencePartnerChange}
					response={response}
				/>}

			{(showSegnalatoreTabs && segnalatoreActiveKey === REPORTER_OPPORTUNITY_TAB) && (
				<div className="mt-2">
					<ReporterOpportunityTab reporterUsername={username} />
				</div>
			)}

			{/* USER LOGS SECTION */}
			{!loading && initialValues.role !== RoleEnum.SEGNALATORE && !newUser && (
				<UserActions
					// responseStoricoAzioni={responseStoricoAzioni}
					itsMe={itsMe}
					columns={actionsColumns}
					data={actionsData}
					loading={loadingLogs}
					error={errorLogs}
				/>
			)}

			<RejectUserModal
				isOpen={showRejectModal}
				doRejectUser={(reason: string) => {
					setShowRejectModal(false);
					rejectUser(reason)
				}}
				closeModal={() => setShowRejectModal(false)}
			/>
		</Container>
	);
};

export default UserFormPage;
